/**
 * Thin wrappers around the orval-generated failure analysis hooks: one
 * failure record of the caller's team with each run's turns, and one of
 * its transcripts whole (routers/failure_analysis.py). Read-only. Same
 * unwrap pattern as useRefineryTree.ts. The record page mounts the
 * transcript's reader only when a turn's transcript is opened.
 */

import type { ApiRequestError } from '@/api/client'
import {
  useReadFailureRecordApiFailureAnalysisRecordsRecordIdGet,
  useReadFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGet,
} from '@/api/generated/failure-analysis/failure-analysis'
import type { FailureRecordResponse, TrainingAgentFile } from '@/api/generated/model'

interface UnwrappedQuery<TData> {
  data: TData | undefined
  isLoading: boolean
  error: ApiRequestError | null
  isError: boolean
}

/** `round` names the copy a verdict's link opens; null reads the one generation planned. */
export function useFailureRecord(
  recordId: string,
  round: string | null,
): UnwrappedQuery<FailureRecordResponse> {
  const query = useReadFailureRecordApiFailureAnalysisRecordsRecordIdGet(
    recordId,
    round === null ? undefined : { round },
    { query: { retry: false } },
  )
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

export function useFailureRecordTranscript(
  recordId: string,
  round: string,
  name: string,
): UnwrappedQuery<TrainingAgentFile> {
  const query = useReadFailureRecordTranscriptApiFailureAnalysisRecordsRecordIdTranscriptsNameGet(
    recordId,
    name,
    { round },
    { query: { retry: false } },
  )
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}
